import type * as GeoJSON from 'geojson';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { createApi } from '@reduxjs/toolkit/query/react';
import { SmartSelection } from '@larki/mp/lib/shape';
// import { MapVersion } from '@larki/shared/type';

import { larkiApi, larkiApiNoAuthentication } from '../../utilities/api';
import { ControllerT } from '@larki/shared/type/lib/controller';
import { processJobList } from '../actions';
import {
  CommitQuoteController,
  CommitRequestForQuoteController,
  EngineQuoteController,
  EnterPaymentStepController,
  PayOrderStageAController,
  PayOrderStageBController,
  PayOrderStageCController,
  SubscribeToMembershipController,
  SavePriceOverridesController,
  GetPriceOverridesController,
  ResetAllPriceOverridesController,
  RestorePriceOverridesFromVersionController,
  ResetPriceOverrideController,
  PreviewResetPriceOverrideController,
  NotifyCustomerOfPriceOverridesController,
} from '@larki/shared/type/controller/order.v2';
import {
  CreateCheckoutLeadController,
  CreatePaymentPageLeadController,
  CreateConfirmationPageLeadController,
  CreatePaidLeadController,
} from '@larki/shared/type/controller/lead.v2';
import {
  GetAssetCollectionStatusController,
  TryDownloadAssetCollectionController,
  GenerateAssetCollectionDownloadUrlController,
} from '@larki/shared/type/controller/asset-collection';
import {
  Generate2dPlanOnMapController,
  Get2dPlanByS3UrlController,
  Get2dPlansOnMapController,
} from '@larki/shared/type/controller/plans';
import { CreateInvoiceActivityController } from '@larki/shared/type/controller/activity';
import {
  OnMembershipPaidController,
  OnOrderPaidController,
} from '@larki/shared/type/controller/deal';
import {
  ListPricingCategoriesController,
  GetPricingByCategoryController,
  BatchUpdatePricingController,
  GetPricingHistoryController,
  RevertPricingChangeController,
  GetAddOnPricingController,
  UpdateAddOnMultiplierController,
} from '@larki/shared/type/controller/pricing';

import { Quote } from '../../mp/services/coverage/workers/quote.worker';
import { redirectTo } from '../../hooks/useRedirect';
import routes from '../../routes/constants';
import { _addMetadata, METADATA_PREFIX } from '../../utilities/bookmark';
import objectUtilities from '../../utilities/object';
import {
  _2dMapCombinedQuote,
  _2dMapQuote,
  CustomOrderQuoteItem,
  PriceOverrideRecord,
} from '@larki/shared/type/order.v2';
import { OrderLayer, OrderLocality } from '@larki/shared/schema';
import { DynamicAppEventT } from '@larki/shared/types/app-event.type';
import { SetMapLockController } from '@larki/shared/type/controller/map2d';
import { Frontiers } from '@larki/shared/types/map2d.type';
import { AssetUploadOrigin } from '@larki/shared/enums';
import { MapType } from '@larki/shared/types/user.type';

export type LarkiErrorResponse = {
  status?: number;
  data: any;
  originalError?: AxiosError;
};

export class LarkiUnauthorizedAccessToJobError extends Error {
  originalError?: AxiosError;
  constructor(originalError?: AxiosError) {
    super('You are not authorized to access this job');
    this.originalError = originalError;
  }
}

type LarkiBaseQueryFnArgs = {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];
};

function larkiApiBaseQuery<T>(): BaseQueryFn<
  LarkiBaseQueryFnArgs,
  T,
  LarkiErrorResponse
> {
  return async ({ url, method, data, params, headers }) => {
    try {
      const result = await larkiApi({
        url,
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.status === 401) {
          redirectTo(routes.login);
        }
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          } satisfies LarkiErrorResponse,
        };
      }
      return {
        error: {
          data: err,
        } satisfies LarkiErrorResponse,
      };
    }
  };
}

function larkiApiNoAuthBaseQuery<T>(): BaseQueryFn<
  LarkiBaseQueryFnArgs,
  T,
  LarkiErrorResponse
> {
  return async ({ url, method, data, params, headers }) => {
    try {
      const result = await larkiApiNoAuthentication({
        url,
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (err) {
      if (err instanceof AxiosError) {
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
            originalError: err,
          },
        };
      }
      return {
        error: {
          data: err,
        },
      };
    }
  };
}

export const RTK_API_REDUCER_PATH = 'rtk';
export const RTK_NO_AUTH_API_REDUCER_PATH = 'rtkNoAuth';

export type Job = {
  id: number;
  status:
    | 'draft'
    | 'pending'
    | 'paid'
    | 'inprogress'
    | 'completed'
    | 'cancelled'
    | 'demo';
  address: string | null;
  full_address: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  place_geometry: GeoJSON.Point | null;
  project: { id: number; name: string | null };
  project_id: number;
  quote: Quote | null;
  anon_order_expiry: Date | null;
  map2d_id?: number;
  owner_user_id: number | null;
  updater_id: number | null;
  customer_software: string | null;
  message: string | null;
  updated_at: string;
  created_at: string;
};

export type JobListing = Job & {
  jobs: Job[];
  expected_at: string | null;
  completed_date: string | null;
  user_project: UserProject;
  project_id: number;
  projectId: number;
  creator: User & { is_anonymous?: boolean };
  updater: User & { is_anonymous?: boolean };
  has_customer: boolean;
  is_other: boolean;
  ui: {
    updateState: 'success' | 'error' | 'pending';
  };
  project: Project & {
    is_other: boolean;
  };
  point_clouds: { id: number; pointerra_id: string }[];
  lga: boolean;
};

export type Project = {
  id: number;
  name: string | null;
  activities: {
    id: number;
    title: string;
    type: string;
    created_at: string;
    updated_at: string;
    visibility: string;
    metadata: Partial<Record<string, string | number>>;
  }[];
  // Only present when asked for: `includeJobs` / `includePointCloud`.
  jobs?: { id: number; status: string; map2d_id: number | null }[];
  point_clouds?: { pointerra_id: string | null }[];
};

export type Bookmark = {
  share_token: string;
  metadata?: Record<string, string>;
  redirect_url: string;
};

// Minimal activity payload matching backend expectations
export type ActivityPayload = {
  type: string;
  title: string;
  description?: string;
  detailedStatusUpdate?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  visibility?: 'default' | 'staff';
  createdAssetIds?: string[];
  supersededAssetIds?: string[];
  removedAssetIds?: string[];
  sendNotification?: boolean;
  jobId?: number;
  projectId?: number;
};

export type UserProject = {
  user_id: number;
  project_id: number;
  invite_status: 'pending' | 'accepted' | 'rejected' | 'requested';
};

type BaseQueryFnCallable<Args extends LarkiBaseQueryFnArgs, Result, Error> = (
  arg: Parameters<BaseQueryFn<Args, Result, Error>>[0]
) => ReturnType<BaseQueryFn<Args, Result, Error>>;
type LarkiQueryFn<T = unknown> = BaseQueryFnCallable<
  LarkiBaseQueryFnArgs,
  T,
  LarkiErrorResponse
>;

export type CompanyProfile = {
  id: number;
  name: string;
  specialization: string;
  address: string;
  phone: string;
  website: string;
  business_number: string;
  logo_url: string;
};

export type User = {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  role?: string;
};

export type MembershipPrice = {
  id: string;
  name: string;
  subtotal: number;
  grand_total: number;
  taxes: {
    display_name: string;
    price: {
      extra: number;
    };
  }[];
};

export type UploadedFile = {
  id: string;
  filename: string;
  metadata?: Record<string, unknown>;
};

export const rtkApi = createApi({
  reducerPath: RTK_API_REDUCER_PATH,
  baseQuery: larkiApiBaseQuery(),
  tagTypes: [
    'Project',

    'User',

    'CompanyProfile',

    'RequestAccess',

    'UserCards',

    'Assets',
    'Activity',

    'PriceOverride',

    'Pricing',
  ],
  endpoints: (build) => ({
    /**
     * Don't use directly, use `useUser` instead.
     */
    getUserProfile: build.query<
      {
        id: number;
        email?: string;
        full_name: string;
        profile_image?: string;
        has_used_trial: boolean;
        role: string;
        customer: {
          memberships: {
            status: string;
            entitlements: string[];
            price_id: string;
          }[];
        };
        map_type: MapType;
        profession?: string;
        should_show_email_banner: boolean;
      },
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      void
    >({
      query: () => ({ url: 'user/profile' }),
      providesTags: ['User'],
      // TODO: use user ID to query to invalidate cache on change user
    }),
    updateLoginEmailBannerFlag: build.mutation<
      void,
      {
        should_show_email_banner: boolean;
      }
    >({
      query: (data) => ({
        url: 'user/login-email-banner-flag',
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['User'],
    }),

    /**
     * Get projects associated with the current user.
     *
     * `getUserProjects` should have to wait until the user has been fetched
     *  so the `isAdmin` value is up to date.
     *
     * Usage:
     * ```
     * const currentUser = ();
     * const {...} = useGetUserProjectsQuery({skip: !currentUser.isSuccess})
     * ```
     */
    getUserProjects: build.query<
      {
        jobToProjectId: Record<number, number>;
        projects: Record<
          number,
          {
            id: number;
            map2d_id?: number;
            created_at: string;
            customer_software: string | null;
            message: string | null;
            project: { id: number; name?: string | null };
            jobs: { id: number; lga: boolean }[];
          }
        >;
      },
      { isAdmin: boolean; includeDemo?: boolean }
    >({
      query: ({ isAdmin, includeDemo = false }) => ({
        url: 'project',
        params: {
          isAdmin,
          includeDemo,
          inviteStatus: ['accepted'],
        },
      }),
      providesTags: ['Project'],
      transformResponse: (data: any) => {
        return {
          jobToProjectId: data.projects?.reduce((acc: any, project: any) => {
            if (project.jobs.length === 0) {
              return acc;
            }
            for (const job of project.jobs) {
              acc[job.id] = project.id;
            }
            return acc;
          }, {}),
          projects: processJobList(data),
          counts: data.statusCounts,
        };
      },
    }),

    /**
     * Create an new job.
     */
    createJob: build.mutation<
      Job,
      {
        address?: string | null;
        city?: string | null;
        state?: string | null;
        postcode?: string | null;
        country?: string | null;
        message?: string | null;
        customer_software?: string | null;
        point_cloud_format?: string | null;
        projectName: string | null;
        mergeProjectId?: number;
        place_geometry?: GeoJSON.Point | null;
        anon_order_expiry?: Date | null;
        map2d_id?: number;
      }
    >({
      query: (data) => ({
        url: 'job',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Project'],
    }),

    /**
     * Duplicates a project and marks the copy as a Sample. Staff only.
     */
    createProjectSample: build.mutation<
      { project: { id: number; name: string | null } },
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/project/${projectId}/sample`,
        method: 'POST',
      }),
      invalidatesTags: ['Project'],
    }),

    /**
     * Drops a project's Sample status without deleting the project. Staff only.
     */
    removeProjectSample: build.mutation<void, { projectId: number }>({
      query: ({ projectId }) => ({
        url: `/project/${projectId}/sample`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Project'],
    }),

    // TODO: add appropriate Response & Request type
    updateJob: build.mutation({
      query: ({ id, data }) => ({
        url: `/job/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Project'],
    }),

    /**
     * Get a job by ID.
     */
    getJob: build.query<JobWithSmartSelections, number>({
      queryFn: async (jobId, _, __, baseQuery) => {
        console.log('getJob', jobId);
        const resp = await (baseQuery as LarkiQueryFn<Job>)({
          url: `/job/${jobId}`,
        });
        if (resp.error) {
          if (resp.error.status === 403) {
            return {
              error: new LarkiUnauthorizedAccessToJobError(
                resp.error.originalError
              ),
            };
          }
          return { error: resp.error };
        }
        const job = resp.data;
        const [smartSelections, property] = await Promise.all([
          new Promise<SmartSelection[]>((resolve, reject) => {
            if (!job.place_geometry) return resolve([]);
            return Promise.resolve(
              baseQuery({
                url: `/engine/product/selections`,
                method: 'POST',
                data: {
                  location: {
                    lng: job.place_geometry.coordinates[0],
                    lat: job.place_geometry.coordinates[1],
                  },
                  address: job.full_address,
                  city: job.city,
                  state: job.state,
                  postcode: job.postcode,
                },
              })
            )
              .then((resp) => {
                if (resp.error) return reject(resp.error);
                const data = resp.data as {
                  property_vicinity: SmartSelection[];
                  neighbourhood: SmartSelection;
                  buildings: SmartSelection[];
                  roads: SmartSelection[];
                };
                return resolve([
                  ...data.property_vicinity,
                  data.neighbourhood,
                  ...data.buildings,
                  ...data.roads,
                ]);
              })
              .catch(reject);
          }),
          new Promise<Property | null>((resolve, reject) => {
            if (!job.place_geometry) return resolve(null);
            return Promise.resolve(
              baseQuery({
                url: '/engine/selections/property',
                method: 'POST',
                data: {
                  location: {
                    lat: job.place_geometry.coordinates[1],
                    lng: job.place_geometry.coordinates[0],
                  },
                },
              })
            )
              .then((resp) => {
                if (resp.error) return reject(resp.error);
                return resolve(resp.data as Property);
              })
              .catch(reject);
          }),
        ]);
        const ret = { ...job, smartSelections, property };
        return { data: ret };
      },
      providesTags: ['Project'],
    }),

    /**
     * Shape downloads.
     */
    geospatialDownload: build.mutation<
      string,
      {
        jobId: number;
        shapes: GeospatialDownloadShapeInput[];
        fileType: GeospatialDownloadFileType;
      }
    >({
      query: ({ jobId, shapes, fileType }) => ({
        url: `/job/${jobId}/geospatial/download`,
        method: 'POST',
        data: {
          shapes,
          fileType,
        },
      }),
    }),

    /**
     * Get a project by ID.
     */
    getProject: build.query<
      Project,
      {
        projectId: number;
        params?: {
          includeJobs?: boolean;
          includePointCloud?: boolean;
          includeQuote?: boolean;
          includeActivities?: boolean;
        };
      }
    >({
      query: ({ projectId, params = {} }) => {
        return {
          url: `/project/${projectId}`,
          params,
        };
      },
      providesTags: (result, _, { params = {} }) => {
        const tags: (
          | { type: 'Project' | 'Activity'; id?: number }
          | 'Project'
        )[] = ['Project'];
        if (params.includeActivities && result?.activities) {
          result.activities.forEach((activity) => {
            tags.push({ type: 'Activity', id: activity.id });
          });
        }
        return tags;
      },
    }),

    /**
     * Check access.
     */
    checkProjectAccess: build.query<
      UserProject & { invite_status_updater: User; created_at?: string },
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/project/${projectId}/check-access`,
      }),
    }),

    checkJobAccess: build.query<
      UserProject & { invite_status_updater: User; created_at?: string },
      { jobId: number }
    >({
      query: ({ jobId }) => ({
        url: `/job/${jobId}/check-access`,
      }),
    }),

    /**
     * Request access to a page, with an existing share token.
     */
    requestAccess: build.mutation<
      unknown,
      { shareToken?: string; projectId?: number; jobId?: number }
    >({
      queryFn: async ({ shareToken, projectId, jobId }, _, __, baseQuery) => {
        const url = shareToken
          ? `/project/bookmark/${shareToken}/request`
          : projectId
            ? `/project/${projectId}/request-access`
            : jobId
              ? `/job/${jobId}/request-access`
              : null;
        if (!url)
          throw new Error('Needed one of [shareToken, projectId, jobId]');
        const resp = await (baseQuery as LarkiQueryFn)({
          url,
          method: 'POST',
        });
        if (resp.error) {
          if (resp.error.status === 409) {
            return { data: null };
          }
          return { error: resp.error };
        }
        return { data: resp.data };
      },
      // if we request access to a project, we need to invalidate its cache
      //  so that the next time we try and view it, we don't get an unauthorized error
      invalidatesTags: ['Project'],
    }),

    /**
     * Open a bookmark.
     */
    openBookmark: build.query<
      { bookmark: Bookmark; isRejected?: boolean; redirectUrl?: string },
      { shareToken: string; redirectToRequestAccess?: boolean }
    >({
      queryFn: async (
        { shareToken, redirectToRequestAccess },
        _,
        __,
        baseQuery
      ) => {
        const resp = await (baseQuery as LarkiQueryFn<{ bookmark: Bookmark }>)({
          url: `/ln/${shareToken}`,
        });
        if (resp.error) {
          if (resp.error.status === 403) {
            const shareToken = resp.error.data?.bookmark?.share_token;
            if (shareToken && redirectToRequestAccess) {
              return {
                data: {
                  ...resp.error.data,
                  redirectUrl: routes.requestAccess.withShareToken(shareToken),
                },
              };
            }
            if (resp.error.data?.userProject?.invite_status === 'rejected') {
              return {
                data: {
                  ...resp.error.data,
                  isRejected: true,
                },
              };
            }
          }
          return { data: resp.error.data };
        }
        const metadata = resp.data.bookmark.metadata;
        const metadataFlat = metadata
          ? objectUtilities.flatten(metadata, METADATA_PREFIX)
          : null;
        const redirectUrl = _addMetadata(
          resp.data.bookmark.redirect_url,
          metadataFlat
        );
        return { data: { ...resp.data, redirectUrl } };
      },
    }),

    reinitiateJob: build.mutation<unknown, { jobId: number }>({
      query: ({ jobId }) => ({
        url: `/job/${jobId}/order/reinitiate`,
        method: 'POST',
      }),
    }),

    supersedeActivity: build.mutation<
      unknown,
      { projectId: number; activityId: number; isSuperseded: boolean }
    >({
      query: ({ projectId, activityId, isSuperseded }) => ({
        url: `/project/${projectId}/activity/${activityId}/supersede`,
        method: 'PATCH',
        data: { isSuperseded },
      }),
      invalidatesTags: (_, __, { activityId }) => [
        { type: 'Activity', id: activityId },
      ],
    }),

    getCompanyProfile: build.query<CompanyProfile & { owner: User }, undefined>(
      {
        query: () => ({
          url: '/organisation/profile',
        }),
        providesTags: ['CompanyProfile'],
      }
    ),

    updateCompanyProfile: build.mutation<
      CompanyProfile & { owner: User },
      Omit<CompanyProfile, 'id'>
    >({
      query: (data) => ({
        url: '/organisation/profile',
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['CompanyProfile'],
    }),

    updateCompanyLogo: build.mutation<unknown, { filePath: string }>({
      query: ({ filePath }) => ({
        url: '/organisation/company-logo',
        method: 'PATCH',
        data: { filePath },
      }),
      invalidatesTags: ['CompanyProfile'],
    }),

    updateOrderProgress: build.mutation<
      unknown,
      { jobId: number; orderProgress: string }
    >({
      query: ({ jobId, orderProgress }) => ({
        url: `/job/${jobId}/order-progress`,
        method: 'PUT',
        data: { orderProgress },
      }),
    }),

    quoteOrder: build.mutation<
      Job,
      {
        jobId: number;
        selections: Selection[];
        includeFreeTierDiscount?: boolean;
        property?: Property | null;
        shouldSetUpdater?: boolean;
        invalidateProject?: boolean;
      }
    >({
      query: ({
        jobId,
        selections,
        includeFreeTierDiscount,
        property,
        shouldSetUpdater = true,
      }) => ({
        url: `/job/${jobId}/order/quote`,
        method: 'POST',
        data: {
          selections,
          ...(property && { property }),
          shouldSetUpdater,
        },
        params: {
          ...(includeFreeTierDiscount && { includeFreeTierDiscount }),
        },
      }),
      invalidatesTags: ['Project'],
    }),

    getLatestProject: build.query<
      Project,
      {
        isAdmin: boolean;
        includeDemo: boolean;
        includeJobs: boolean;
        includePointCloud: boolean;
      }
    >({
      query: ({ isAdmin, includeDemo, includeJobs, includePointCloud }) => ({
        url: '/project/latest',
        params: {
          isAdmin,
          includeDemo,
          includeJobs,
          includePointCloud,
        },
      }),
      providesTags: (result) =>
        result ? [{ type: 'Project', id: result.id }, 'Project'] : ['Project'],
    }),

    getRequestAccessCount: build.query<number, { projectId: number }>({
      query: ({ projectId }) => ({
        url: `/project/${projectId}/request-access/count`,
      }),
      providesTags: (_, __, arg) => [
        { type: 'RequestAccess', id: arg.projectId },
        'RequestAccess',
      ],
    }),

    listRequests: build.query<
      {
        data: {
          user: {
            id: number;
            full_name: string;
            email: string;
          };
          project: {
            id: number;
            name: string;
            jobs: {
              id: number;
              full_address: string;
            }[];
          };
        }[];
      },
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/project/${projectId}/request-access`,
      }),
      providesTags: (_, __, arg) => [
        { type: 'RequestAccess', id: arg.projectId },
        'RequestAccess',
      ],
    }),

    updateRequestAccess: build.mutation<
      unknown,
      { projectId: number; userId: number; status: 'accepted' | 'rejected' }
    >({
      query: ({ projectId, userId, status }) => ({
        url: `/project/${projectId}/request-access`,
        method: 'PUT',
        data: {
          user_id: userId,
          status,
        },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: 'RequestAccess', id: arg.projectId },
        'RequestAccess',
      ],
    }),

    createBookmark: build.mutation<
      { url: string },
      {
        projectId: number;
        redirectUrl: string;
        metadata?: Record<string, string>;
      }
    >({
      query: ({ projectId, redirectUrl, metadata }) => ({
        url: `/project/bookmark/${projectId}`,
        method: 'POST',
        data: {
          redirect_url: redirectUrl,
          metadata,
        },
      }),
    }),

    quote2dMap: build.query<
      _2dMapCombinedQuote,
      {
        layers: OrderLayer[];
        locality: OrderLocality;
        asMember?: boolean | null;
        membershipPriceId?: string | null;
        jobId?: number | null;
        map2dVersionId?: number;
        customOrders?: CustomOrderQuoteItem[];
        excludeOverrideProducts?: string[];
        honorPriceLock?: boolean;
      }
    >({
      query: (data) => ({
        url: '/order-v2/quote',
        method: 'POST',
        data,
      }),
    }),

    get2dMapStateFromJob: build.mutation<
      { bytes: string | null },
      { jobId: number }
    >({
      query: ({ jobId }) => ({
        url: `/map2d/state-from-job/${jobId}`,
      }),
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getMembershipPrices: build.query<MembershipPrice[], void>({
      query: () => ({
        url: '/user/prices',
      }),
    }),

    getPricingCategories: build.query<
      ControllerT<ListPricingCategoriesController>['resBody'],
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      void
    >({
      query: () => ({ url: 'pricing/categories' }),
      providesTags: ['Pricing'],
    }),

    getPricingByCategory: build.query<
      ControllerT<GetPricingByCategoryController>['resBody'],
      ControllerT<GetPricingByCategoryController>['params']['categoryId']
    >({
      query: (categoryId) => ({ url: `pricing/category/${categoryId}` }),
      providesTags: ['Pricing'],
    }),

    batchUpdatePricing: build.mutation<
      ControllerT<BatchUpdatePricingController>['resBody'],
      ControllerT<BatchUpdatePricingController>['params'] & {
        data: ControllerT<BatchUpdatePricingController>['reqBody'];
      }
    >({
      query: ({ categoryId, data }) => ({
        url: `pricing/category/${categoryId}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['Pricing'],
    }),

    getPricingHistory: build.query<
      ControllerT<GetPricingHistoryController>['resBody'],
      ControllerT<GetPricingHistoryController>['params']['categoryId']
    >({
      query: (categoryId) => ({ url: `pricing/category/${categoryId}/history` }),
      providesTags: ['Pricing'],
    }),

    revertPricingChange: build.mutation<
      ControllerT<RevertPricingChangeController>['resBody'],
      ControllerT<RevertPricingChangeController>['params']['changeId']
    >({
      query: (changeId) => ({
        url: `pricing/history/${changeId}/revert`,
        method: 'POST',
      }),
      // A no-op revert writes nothing server-side, so skip the refetch.
      invalidatesTags: (result) => (result?.applied ? ['Pricing'] : []),
    }),

    getAddOnPricing: build.query<
      ControllerT<GetAddOnPricingController>['resBody'],
      ControllerT<GetAddOnPricingController>['params']['categoryId']
    >({
      query: (categoryId) => ({ url: `pricing/add-on/${categoryId}` }),
      providesTags: ['Pricing'],
    }),

    updateAddOnMultiplier: build.mutation<
      ControllerT<UpdateAddOnMultiplierController>['resBody'],
      ControllerT<UpdateAddOnMultiplierController>['params'] & {
        data: ControllerT<UpdateAddOnMultiplierController>['reqBody'];
      }
    >({
      query: ({ categoryId, data }) => ({
        url: `pricing/add-on/${categoryId}/multiplier`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['Pricing'],
    }),

    getUserCards: build.query<
      {
        id: string;
        default_payment_method: string;
        brand: string;
        last4: string;
        name: string;
      }[],
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      void
    >({
      query: () => ({
        url: '/user/cards',
      }),
      providesTags: ['UserCards'],
    }),

    removePaymentMethod: build.mutation<unknown, { paymentMethodId: string }>({
      query: ({ paymentMethodId }) => ({
        url: `/user/payment-method`,
        params: { paymentMethodId },
        method: 'DELETE',
      }),
      invalidatesTags: ['UserCards'],
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    createPaymentMethodSetup: build.mutation<string, void>({
      query: () => ({
        url: '/user/payment-method-setup',
        method: 'POST',
      }),
    }),

    // TODO: this does not exist anymore => remove
    commitOrder: build.mutation<
      unknown,
      {
        quote: _2dMapCombinedQuote;
        jobId: number;
        outputFormat: string;
      }
    >({
      query: ({ jobId, outputFormat, quote }) => ({
        url: `/order-v2/${jobId}/commit`,
        method: 'POST',
        data: { outputFormat, quote },
      }),
    }),

    commitQuote: build.mutation<
      ControllerT<CommitQuoteController>['resBody'],
      {
        jobId: number;
        data: ControllerT<CommitQuoteController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/commit/quote`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Project'],
    }),

    commitRequestForQuote: build.mutation<
      ControllerT<CommitRequestForQuoteController>['resBody'],
      {
        jobId: number;
        data: ControllerT<CommitRequestForQuoteController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/commit/rfq`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Project'],
    }),

    payOrderStageA: build.mutation<
      ControllerT<PayOrderStageAController>['resBody'],
      {
        data: ControllerT<PayOrderStageAController>['reqBody'];
        quoteId: number;
      }
    >({
      query: ({ data, quoteId }) => ({
        url: `/order-v2/quote/${quoteId}/pay/a`,
        method: 'POST',
        data,
      }),
    }),

    payOrderStageB: build.mutation<
      ControllerT<PayOrderStageBController>['resBody'],
      {
        quoteId: number;
        data: ControllerT<PayOrderStageBController>['reqBody'];
      }
    >({
      query: ({ quoteId, data }) => ({
        url: `/order-v2/quote/${quoteId}/pay/b`,
        method: 'POST',
        data,
      }),
    }),

    payOrderStageC: build.mutation<
      ControllerT<PayOrderStageCController>['resBody'],
      {
        quoteId: number;
        data: ControllerT<PayOrderStageCController>['reqBody'];
      }
    >({
      query: ({ quoteId, data }) => ({
        url: `/order-v2/quote/${quoteId}/pay/c`,
        method: 'POST',
        data,
      }),
    }),

    enterPaymentStep: build.mutation<
      ControllerT<EnterPaymentStepController>['resBody'],
      ControllerT<EnterPaymentStepController>['params'] & {
        data: ControllerT<EnterPaymentStepController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/enter-payment-step`,
        method: 'POST',
        data,
      }),
    }),

    subscribeToMembership: build.mutation<
      ControllerT<SubscribeToMembershipController>['resBody'],
      ControllerT<SubscribeToMembershipController>['reqBody']
    >({
      invalidatesTags: ['User'],
      query: (data) => ({
        url: '/order-v2/subscribe',
        method: 'POST',
        data,
      }),
    }),

    initiateQuote: build.mutation<
      unknown,
      {
        quoteId: number;
      }
    >({
      query: ({ quoteId }) => ({
        url: `/order-v2/quote/${quoteId}/initiate`,
        method: 'POST',
      }),
    }),

    createQuoteActivity: build.mutation<
      unknown,
      {
        quoteId: number;
      }
    >({
      query: ({ quoteId }) => ({
        url: `/order-v2/quote/${quoteId}/create-activity`,
        method: 'POST',
      }),
    }),

    getMap2d: build.query<
      {
        id: number;
        bytes: string | null;
      },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/map2d/${id}`,
      }),
    }),

    replayMap2d: build.mutation<
      { id: number; bytes: string },
      { id: number; frontiers: Frontiers }
    >({
      query: ({ id, frontiers }) => ({
        url: `/map2d/${id}/replay`,
        method: 'POST',
        data: { frontiers },
      }),
    }),

    cancelDownloadAll: build.mutation<
      { status?: string; cancelled: boolean },
      { projectId: number; queueJobId: string }
    >({
      query: ({ projectId, queueJobId }) => ({
        url: `/project/${projectId}/cancel-download-all`,
        method: 'POST',
        data: { queueJobId },
      }),
    }),

    sendCheckoutLead: build.mutation<
      unknown,
      {
        data: ControllerT<CreateCheckoutLeadController>['reqBody'];
        jobId: number;
      }
    >({
      query: ({ data, jobId }) => ({
        url: `/lead-v2/${jobId}/send-checkout`,
        method: 'POST',
        data,
      }),
    }),

    sendPaymentPageLead: build.mutation<
      unknown,
      {
        data: ControllerT<CreatePaymentPageLeadController>['reqBody'];
        jobId: number;
      }
    >({
      query: ({ data, jobId }) => ({
        url: `/lead-v2/${jobId}/send-payment-page`,
        method: 'POST',
        data,
      }),
    }),

    sendConfirmationPageLead: build.mutation<
      unknown,
      {
        data: ControllerT<CreateConfirmationPageLeadController>['reqBody'];
        jobId: number;
      }
    >({
      query: ({ data, jobId }) => ({
        url: `/lead-v2/${jobId}/send-confirmation-page`,
        method: 'POST',
        data,
      }),
    }),

    sendPaidLead: build.mutation<
      unknown,
      { data: ControllerT<CreatePaidLeadController>['reqBody']; jobId: number }
    >({
      query: ({ data, jobId }) => ({
        url: `/lead-v2/${jobId}/send-paid`,
        method: 'POST',
        data,
      }),
    }),

    createAppEvent: build.mutation<
      unknown,
      { jobId: number; dynamic: DynamicAppEventT }
    >({
      query: ({ jobId, dynamic }) => ({
        url: `/app-event/${jobId}`,
        method: 'POST',
        data: { dynamic },
      }),
    }),

    toggleActivityIsHidden: build.mutation<
      unknown,
      { projectId: number; activityId: number; isHidden: boolean }
    >({
      query: ({ projectId, activityId, isHidden }) => ({
        url: `/project/${projectId}/activity/${activityId}/toggle-hidden`,

        method: 'PATCH',
        data: { isHidden },
      }),
      invalidatesTags: (_, __, { activityId }) => [
        { type: 'Activity', id: activityId },
      ],
    }),

    deleteActivity: build.mutation<
      unknown,
      { projectId: number; activityId: number }
    >({
      query: ({ projectId, activityId }) => ({
        url: `/project/${projectId}/activity/${activityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Activity'],
    }),

    setMapLock: build.mutation<
      ControllerT<SetMapLockController>['resBody'],
      Pick<ControllerT<SetMapLockController>, 'params' | 'reqBody'>
    >({
      query: ({ params: { jobId }, reqBody: data }) => ({
        url: `/map2d/${jobId}/set-map-lock`,
        method: 'POST',
        data,
      }),
    }),
    createProjectAsset: build.mutation<
      { asset_id: string }, // return asset_id
      {
        projectId: number;
        filename: string;
        contentType: string;
        metadata?: {
          uploadOrigin?: AssetUploadOrigin;
        };
        jobId?: number;
      }
    >({
      query: ({ projectId, filename, contentType, metadata, jobId }) => ({
        url: `/project/${projectId}/asset`,
        method: 'POST',
        data: { filename, contentType, metadata, jobId },
      }),
    }),

    commitProjectAsset: build.mutation<
      unknown,
      { projectId: number; assetId: string }
    >({
      query: ({ projectId, assetId }) => ({
        url: `/project/${projectId}/asset/${assetId}/commit`,
        method: 'PUT',
      }),
    }),

    createActivity: build.mutation<
      { id: number },
      { projectId: number; activity: ActivityPayload }
    >({
      query: ({ projectId, activity }) => ({
        url: `/project/${projectId}/activity`,
        method: 'POST',
        data: activity,
      }),
      invalidatesTags: ['Project'],
    }),

    getUploadedFiles: build.query<
      UploadedFile[],
      { projectId: number; jobId: number; uploadOrigin: AssetUploadOrigin }
    >({
      query: ({ projectId, jobId, uploadOrigin }) => ({
        url: `/project/${projectId}/assets`,
        method: 'POST',
        data: { jobId, uploadOrigin },
      }),
      providesTags: (result, __, { uploadOrigin }) => {
        const baseTags = [
          'Assets' as const,
          { type: 'Assets' as const, id: uploadOrigin },
        ];
        if (!result) return baseTags;
        return [
          ...baseTags,
          ...result.map((file) => ({ type: 'Assets' as const, id: file.id })),
        ];
      },
    }),

    deleteUploadedFile: build.mutation<
      { activityId: number },
      { projectId: number; assetId: string }
    >({
      query: ({ projectId, assetId }) => ({
        url: `/project/${projectId}/asset/${assetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, __, { assetId }) => {
        const tags: (
          | 'Assets'
          | { type: 'Assets'; id: string }
          | { type: 'Activity'; id: number }
        )[] = ['Assets', { type: 'Assets', id: assetId }];
        if (result?.activityId) {
          tags.push({ type: 'Activity', id: result.activityId });
        }
        return tags;
      },
    }),

    engineQuote: build.mutation<
      unknown,
      ControllerT<EngineQuoteController>['reqBody']
    >({
      query: ({ jobId }) => ({
        url: `/order-v2/${jobId}/engine-quote`,
        method: 'POST',
      }),
    }),

    savePriceOverrides: build.mutation<
      ControllerT<SavePriceOverridesController>['resBody'],
      {
        jobId: number;
        data: ControllerT<SavePriceOverridesController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/price-overrides`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { jobId }) => [
        { type: 'PriceOverride', id: `JOB:${jobId}` },
        'Project',
      ],
    }),

    getPriceOverrides: build.query<
      ControllerT<GetPriceOverridesController>['resBody'],
      { jobId: number; map2dVersionId?: number }
    >({
      query: ({ jobId, map2dVersionId }) => ({
        url: `/order-v2/${jobId}/price-overrides`,
        method: 'GET',
        params: map2dVersionId != null ? { map2dVersionId } : undefined,
      }),
      providesTags: (_res, _err, { jobId, map2dVersionId }) => [
        {
          type: 'PriceOverride',
          id: map2dVersionId != null ? `${jobId}:${map2dVersionId}` : jobId,
        },
        { type: 'PriceOverride', id: `JOB:${jobId}` },
      ],
    }),

    resetAllPriceOverrides: build.mutation<
      { newVersionId: number },
      {
        jobId: number;
        data: ControllerT<ResetAllPriceOverridesController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/price-overrides/reset`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { jobId }) => [
        { type: 'PriceOverride', id: `JOB:${jobId}` },
        'Project',
      ],
    }),

    resetPriceOverride: build.mutation<
      { newVersionId: number },
      {
        jobId: number;
        data: ControllerT<ResetPriceOverrideController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/price-overrides/reset`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_res, _err, { jobId }) => [
        { type: 'PriceOverride', id: `JOB:${jobId}` },
        'Project',
      ],
    }),

    previewResetPriceOverride: build.mutation<
      ControllerT<PreviewResetPriceOverrideController>['resBody'],
      {
        jobId: number;
        data: ControllerT<PreviewResetPriceOverrideController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/price-overrides/preview-reset`,
        method: 'POST',
        data,
      }),
    }),

    restorePriceOverridesFromVersion: build.mutation<
      ControllerT<RestorePriceOverridesFromVersionController>['resBody'],
      {
        jobId: number;
        data: ControllerT<RestorePriceOverridesFromVersionController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/price-overrides/restore-from-version`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, { jobId }) => [
        { type: 'PriceOverride', id: `JOB:${jobId}` },
      ],
    }),

    notifyCustomerOfPriceOverrides: build.mutation<
      ControllerT<NotifyCustomerOfPriceOverridesController>['resBody'],
      {
        jobId: number;
        data?: ControllerT<NotifyCustomerOfPriceOverridesController>['reqBody'];
      }
    >({
      query: ({ jobId, data }) => ({
        url: `/order-v2/${jobId}/price-overrides/notify-customer`,
        method: 'POST',
        data: data ?? {},
      }),
    }),

    tryDownloadAssetCollection: build.mutation<
      ControllerT<TryDownloadAssetCollectionController>['resBody'],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/asset-collection/${projectId}/try-download`,
        method: 'POST',
      }),
    }),

    getAssetCollectionStatus: build.mutation<
      ControllerT<GetAssetCollectionStatusController>['resBody'],
      ControllerT<GetAssetCollectionStatusController>['reqQuery']
    >({
      query: (params) => ({
        url: `/asset-collection/status`,
        method: 'GET',
        params,
      }),
    }),

    generateAssetCollectionDownloadUrl: build.mutation<
      ControllerT<GenerateAssetCollectionDownloadUrlController>['resBody'],
      ControllerT<GenerateAssetCollectionDownloadUrlController>['reqBody']
    >({
      query: (params) => ({
        url: `/asset-collection/generate-download-url`,
        method: 'POST',
        data: params,
      }),
    }),

    getCombinedQuoteForVersion: build.query<
      _2dMapCombinedQuote,
      { versionId: number }
    >({
      query: ({ versionId }) => ({
        url: `/order-v2/${versionId}/combined-quote`,
      }),
    }),

    getCombinedQuoteForQuote: build.query<
      _2dMapCombinedQuote,
      { quoteId: number }
    >({
      query: ({ quoteId }) => ({
        url: `/order-v2/quote/${quoteId}/combined`,
      }),
    }),

    get2dPlan: build.mutation<
      ControllerT<Get2dPlanByS3UrlController>['resBody'],
      { jobId: number; planS3Url: string }
    >({
      query: ({ jobId, planS3Url }) => ({
        url: `/plans/${jobId}`,
        method: 'GET',
        params: { planS3Url },
      }),
    }),

    generate2dPlanOnMap: build.mutation<
      ControllerT<Generate2dPlanOnMapController>['resBody'],
      ControllerT<Generate2dPlanOnMapController>['reqBody'] & {
        projectId: number;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/plans/${projectId}/generate`,
        method: 'POST',
        data: body,
      }),
      // Patch the cache instead of invalidating: a fulfilled place proves the shape exists.
      async onQueryStarted({ projectId, assetId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            rtkApi.util.updateQueryData(
              'get2dPlansOnMap',
              { projectId },
              (draft) => {
                // draft is undefined if the query hasn't resolved yet.
                if (draft && !draft.assetIds.includes(assetId)) {
                  draft.assetIds.push(assetId);
                }
              }
            )
          );
        } catch {
          // Mutation failed: the shape wasn't placed, so leave the cache untouched.
        }
      },
    }),

    get2dPlansOnMap: build.query<
      ControllerT<Get2dPlansOnMapController>['resBody'],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `/plans/${projectId}/placed`,
        method: 'GET',
      }),
    }),

    createInvoiceActivity: build.mutation<
      ControllerT<CreateInvoiceActivityController>['resBody'],
      { quoteId: number; amount: number }
    >({
      query: ({ quoteId, amount }) => ({
        url: `/order-v2/quote/${quoteId}/create-invoice-activity`,
        method: 'POST',
        data: { amount },
      }),
      invalidatesTags: ['Project'],
    }),

    createDealForOrderPaid: build.mutation<
      ControllerT<OnOrderPaidController>['resBody'],
      { jobId: number; quoteId: number }
    >({
      query: ({ jobId, quoteId }) => ({
        url: `/deal/order/${jobId}/${quoteId}/won`,
        method: 'POST',
      }),
    }),

    createDealForMembershipPaid: build.mutation<
      ControllerT<OnMembershipPaidController>['resBody'],
      { priceId: string; recurringFrequency: string }
    >({
      query: ({ priceId, recurringFrequency }) => ({
        url: `/deal/membership/won`,
        method: 'POST',
        data: { priceId, recurringFrequency },
      }),
    }),

    promoteFreeMapToJob: build.mutation<
      { jobId: number; map2dId: number; projectId: number },
      { key: string; intent?: string; shapes: unknown[] }
    >({
      query: ({ key, intent, shapes }) => ({
        url: `/free-map/${key}/promote`,
        method: 'POST',
        data: { intent, shapes },
      }),
    }),
  }),
});

export const rtkNoAuthApi = createApi({
  reducerPath: RTK_NO_AUTH_API_REDUCER_PATH,
  baseQuery: larkiApiNoAuthBaseQuery(),
  tagTypes: ['User', 'FreeMap'],
  endpoints: (build) => ({
    verifyToken: build.mutation<
      User & {
        image: string;
        is_anonymous: boolean;
        token: string;
      },
      { token: string }
    >({
      query: ({ token }) => ({
        url: '/user/verify-token',
        method: 'POST',
        data: { token },
      }),
      invalidatesTags: ['User'],
    }),

    createAnonymousAndValidate: build.mutation<
      {
        user: User & {
          image: string;
          is_anonymous: boolean;
          token: string;
        };
      },
      { shareToken: string; allowedAnonymousRoutes: string[] }
    >({
      query: ({ shareToken, allowedAnonymousRoutes }) => ({
        url: '/user/create-anonymous-and-validate',
        method: 'POST',
        data: { shareToken, allowedAnonymousRoutes },
      }),
      invalidatesTags: ['User'],
    }),

    createFreeMapKey: build.mutation<
      { free_map_key: string },
      {
        address: string;
        city: string;
        state: string;
        postcode: string;
        country: string;
        place_geometry: GeoJSON.Point;
      }
    >({
      query: ({ address, city, state, postcode, country, place_geometry }) => ({
        url: '/free-map',
        method: 'POST',
        data: { address, city, state, postcode, country, place_geometry },
      }),
    }),

    getFreeMapSession: build.query<
      {
        full_address: string;
        address: string | null;
        city: string | null;
        state: string | null;
        postcode: string | null;
        country: string | null;
        place_geometry: GeoJSON.Geometry | null;
      },
      { key: string }
    >({
      query: ({ key }) => ({
        url: `/free-map/${key}`,
      }),
      providesTags: (_result, __, { key }) => [{ type: 'FreeMap', id: key }],
    }),

    getPublicProperty: build.query<
      Property | null,
      { location: { lat: number; lng: number } }
    >({
      query: ({ location }) => ({
        url: '/engine/selections/property/public',
        method: 'POST',
        data: { location },
      }),
    }),
  }),
});

interface JobWithSmartSelections extends Job {
  smartSelections: SmartSelection[];
  property: Property | null;
}

export type Selection = {
  id: string;
  region: GeoJSON.Polygon;
  ui_state: {
    name: string;
    type: string;
  };
  created_at: string | null;
  category_name: string;
  parent_selection_id: string;
};

export type Property = {
  geometry: GeoJSON.Polygon;
};
export type GeospatialDownloadShapeInput = GeoJSON.Feature<
  GeoJSON.Polygon,
  { name?: string | null }
>;

export type GeospatialDownloadFileType =
  | 'geojson'
  | 'shp'
  | 'kml'
  | 'dxf'
  | 'dxf-truncated';

export const {
  useGetUserProfileQuery,
  useUpdateLoginEmailBannerFlagMutation,
  useGetUserProjectsQuery,
  useCreateJobMutation,
  useCreateProjectSampleMutation,
  useRemoveProjectSampleMutation,
  useUpdateJobMutation,
  useGetJobQuery,
  useLazyGetJobQuery,
  useGeospatialDownloadMutation,
  useGetProjectQuery,
  useCheckProjectAccessQuery,
  useCheckJobAccessQuery,
  useRequestAccessMutation,
  useLazyOpenBookmarkQuery,
  useOpenBookmarkQuery,
  useReinitiateJobMutation,
  useSupersedeActivityMutation,
  useGetCompanyProfileQuery,
  useUpdateCompanyProfileMutation,
  useUpdateCompanyLogoMutation,
  useUpdateOrderProgressMutation,
  useQuoteOrderMutation,
  useGetLatestProjectQuery,
  useGetRequestAccessCountQuery,
  useListRequestsQuery,
  useUpdateRequestAccessMutation,
  useCreateBookmarkMutation,
  useLazyQuote2dMapQuery,
  useGet2dMapStateFromJobMutation,
  useGetMembershipPricesQuery,
  useGetPricingCategoriesQuery,
  useGetPricingByCategoryQuery,
  useBatchUpdatePricingMutation,
  useGetPricingHistoryQuery,
  useRevertPricingChangeMutation,
  useGetAddOnPricingQuery,
  useUpdateAddOnMultiplierMutation,
  useGetUserCardsQuery,
  useRemovePaymentMethodMutation,
  useCreatePaymentMethodSetupMutation,
  useCommitOrderMutation,
  useCommitQuoteMutation,
  useCommitRequestForQuoteMutation,
  usePayOrderStageAMutation,
  usePayOrderStageBMutation,
  usePayOrderStageCMutation,
  useEnterPaymentStepMutation,
  useInitiateQuoteMutation,
  useCreateQuoteActivityMutation,
  useLazyGetMap2dQuery,
  useReplayMap2dMutation,
  useCancelDownloadAllMutation,
  useSendCheckoutLeadMutation,
  useSendPaymentPageLeadMutation,
  useSendConfirmationPageLeadMutation,
  useSendPaidLeadMutation,
  useCreateAppEventMutation,
  useToggleActivityIsHiddenMutation,
  useDeleteActivityMutation,
  useSetMapLockMutation,
  useCreateProjectAssetMutation,
  useCommitProjectAssetMutation,
  useCreateActivityMutation,
  useGetUploadedFilesQuery,
  useDeleteUploadedFileMutation,
  useSubscribeToMembershipMutation,
  useEngineQuoteMutation,
  useSavePriceOverridesMutation,
  useGetPriceOverridesQuery,
  useResetAllPriceOverridesMutation,
  useResetPriceOverrideMutation,
  usePreviewResetPriceOverrideMutation,
  useRestorePriceOverridesFromVersionMutation,
  useNotifyCustomerOfPriceOverridesMutation,
  useTryDownloadAssetCollectionMutation,
  useGetAssetCollectionStatusMutation,
  useGenerateAssetCollectionDownloadUrlMutation,
  useLazyGetCombinedQuoteForVersionQuery,
  useLazyGetCombinedQuoteForQuoteQuery,
  useGet2dPlanMutation,
  useGenerate2dPlanOnMapMutation,
  useGet2dPlansOnMapQuery,
  useCreateInvoiceActivityMutation,
  useCreateDealForOrderPaidMutation,
  useCreateDealForMembershipPaidMutation,
  usePromoteFreeMapToJobMutation,
} = rtkApi;

export const {
  useVerifyTokenMutation,
  useCreateAnonymousAndValidateMutation,
  useCreateFreeMapKeyMutation,
  useGetFreeMapSessionQuery,
  useGetPublicPropertyQuery,
} = rtkNoAuthApi;
